import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { getCurrentAuthUser } from "./authRepository";
import {
  RELATIONSHIP_STATUSES,
  RELATIONSHIP_TYPES,
  relationshipIdFromPairKey,
} from "../domain/playerRelationship/playerRelationshipTypes";

const COLLECTION = "playerRelationships";

function relRef(id) {
  return doc(db, COLLECTION, id);
}

function fromSnap(snap) {
  if (!snap.exists()) return null;
  return { ...snap.data(), relationshipId: snap.id };
}

function mapDocs(snap) {
  return snap.docs.map((d) => ({ ...d.data(), relationshipId: d.id }));
}

function mergeById(rows) {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (row?.relationshipId) byId.set(row.relationshipId, row);
  });
  return [...byId.values()];
}

function sortByUpdatedDesc(rows) {
  return [...(rows || [])].sort(
    (a, b) =>
      (Number(b.updatedAtMs) || Number(b.createdAtMs) || 0) -
      (Number(a.updatedAtMs) || Number(a.createdAtMs) || 0)
  );
}

/**
 * Prefer edges involving playerId; if none match (playerId drift), keep uid hits.
 * @param {object[]} rows
 * @param {string | null | undefined} playerId
 */
function filterForPlayer(rows, playerId) {
  if (!playerId) return rows;
  const filtered = rows.filter(
    (r) =>
      r.fromPlayerId === playerId ||
      r.toPlayerId === playerId ||
      r.playerId === playerId ||
      r.relatedPlayerId === playerId ||
      (r.memberPlayerIds || []).includes(playerId)
  );
  return filtered.length > 0 ? filtered : rows;
}

/**
 * @param {object} relationship
 */
export async function createPlayerRelationship(relationship) {
  const id = relationship.relationshipId;
  if (!id) throw new Error("relationshipId required");
  await setDoc(relRef(id), {
    ...relationship,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return relationship;
}

/**
 * @param {string} relationshipId
 */
export async function fetchPlayerRelationship(relationshipId) {
  if (!relationshipId) return null;
  return fromSnap(await getDoc(relRef(relationshipId)));
}

/**
 * @param {string} pairKey
 */
export async function findRelationshipByPairKey(pairKey) {
  if (!pairKey) return null;
  return fetchPlayerRelationship(relationshipIdFromPairKey(pairKey));
}

/**
 * @param {string} relationshipId
 * @param {object} patch
 */
export async function updatePlayerRelationship(relationshipId, patch) {
  await updateDoc(relRef(relationshipId), {
    ...patch,
    updatedAtMs: Date.now(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Accepted friends for the signed-in user.
 * Uses fromUserId + toUserId queries (rules-safe) instead of memberUserIds
 * array-contains, which Firestore rejects unless list rules mention that field.
 *
 * @param {string} playerId
 * @param {{ max?: number; userId?: string | null }} [options]
 */
export async function listAcceptedFriendsForPlayer(playerId, options = {}) {
  const max = Math.min(options.max || 80, 120);
  const uid = options.userId || getCurrentAuthUser()?.uid || null;
  if (!uid) return [];

  const base = [
    where("relationshipType", "==", RELATIONSHIP_TYPES.FRIEND),
    where("status", "==", RELATIONSHIP_STATUSES.ACCEPTED),
    orderBy("createdAtMs", "desc"),
    limit(max),
  ];

  const [asFrom, asTo] = await Promise.all([
    getDocs(
      query(
        collection(db, COLLECTION),
        where("fromUserId", "==", uid),
        ...base
      )
    ),
    getDocs(
      query(
        collection(db, COLLECTION),
        where("toUserId", "==", uid),
        ...base
      )
    ),
  ]);

  const rows = sortByUpdatedDesc(
    filterForPlayer(mergeById([...mapDocs(asFrom), ...mapDocs(asTo)]), playerId)
  );
  return rows.slice(0, max);
}

/**
 * Incoming pending friend requests for the signed-in user.
 * @param {string} toPlayerId kept for API compatibility; filtered client-side if needed
 * @param {{ max?: number; userId?: string | null }} [options]
 */
export async function listPendingReceivedForPlayer(toPlayerId, options = {}) {
  const max = Math.min(options.max || 40, 80);
  const uid = options.userId || getCurrentAuthUser()?.uid || null;
  if (!uid) return [];
  const qref = query(
    collection(db, COLLECTION),
    where("toUserId", "==", uid),
    where("relationshipType", "==", RELATIONSHIP_TYPES.FRIEND),
    where("status", "==", RELATIONSHIP_STATUSES.PENDING),
    orderBy("createdAtMs", "desc"),
    limit(max)
  );
  const snap = await getDocs(qref);
  const rows = mapDocs(snap);
  if (!toPlayerId) return rows;
  const filtered = rows.filter((r) => r.toPlayerId === toPlayerId);
  return filtered.length > 0 ? filtered : rows;
}

/**
 * Outgoing pending friend requests.
 * @param {string} fromPlayerId
 * @param {{ max?: number; userId?: string | null }} [options]
 */
export async function listPendingSentForPlayer(fromPlayerId, options = {}) {
  const max = Math.min(options.max || 40, 80);
  const uid = options.userId || getCurrentAuthUser()?.uid || null;
  if (!uid) return [];
  const qref = query(
    collection(db, COLLECTION),
    where("fromUserId", "==", uid),
    where("relationshipType", "==", RELATIONSHIP_TYPES.FRIEND),
    where("status", "==", RELATIONSHIP_STATUSES.PENDING),
    orderBy("createdAtMs", "desc"),
    limit(max)
  );
  const snap = await getDocs(qref);
  const rows = mapDocs(snap);
  if (!fromPlayerId) return rows;
  const filtered = rows.filter((r) => r.fromPlayerId === fromPlayerId);
  return filtered.length > 0 ? filtered : rows;
}

/**
 * Relationships involving the viewer (accepted + pending) for search enrichment.
 * @param {string} playerId
 * @param {{ max?: number; userId?: string | null }} [options]
 */
export async function listRelationshipsForPlayer(playerId, options = {}) {
  const max = Math.min(options.max || 120, 150);
  const uid = options.userId || getCurrentAuthUser()?.uid || null;
  if (!uid) return [];

  const [accepted, received, sent] = await Promise.all([
    listAcceptedFriendsForPlayer(playerId, { userId: uid, max }),
    listPendingReceivedForPlayer(playerId, { userId: uid, max }),
    listPendingSentForPlayer(playerId, { userId: uid, max }),
  ]);

  return sortByUpdatedDesc(
    filterForPlayer(mergeById([...accepted, ...received, ...sent]), playerId)
  ).slice(0, max);
}

/**
 * @param {string} toUserId
 * @param {(list: object[]) => void} onChange
 */
export function subscribePendingFriendRequestsForUser(toUserId, onChange) {
  if (!toUserId) {
    onChange([]);
    return () => {};
  }
  const qref = query(
    collection(db, COLLECTION),
    where("toUserId", "==", toUserId),
    where("relationshipType", "==", RELATIONSHIP_TYPES.FRIEND),
    where("status", "==", RELATIONSHIP_STATUSES.PENDING),
    orderBy("createdAtMs", "desc"),
    limit(40)
  );
  return onSnapshot(
    qref,
    (snap) => {
      onChange(mapDocs(snap));
    },
    () => onChange([])
  );
}

/**
 * @param {string} userId
 * @param {(list: object[]) => void} onChange
 */
export function subscribeAcceptedFriends(userId, onChange) {
  if (!userId) {
    onChange([]);
    return () => {};
  }

  const base = [
    where("relationshipType", "==", RELATIONSHIP_TYPES.FRIEND),
    where("status", "==", RELATIONSHIP_STATUSES.ACCEPTED),
    orderBy("createdAtMs", "desc"),
    limit(80),
  ];

  let fromRows = [];
  let toRows = [];

  const emit = () => {
    onChange(sortByUpdatedDesc(mergeById([...fromRows, ...toRows])));
  };

  const unsubFrom = onSnapshot(
    query(
      collection(db, COLLECTION),
      where("fromUserId", "==", userId),
      ...base
    ),
    (snap) => {
      fromRows = mapDocs(snap);
      emit();
    },
    () => {
      fromRows = [];
      emit();
    }
  );

  const unsubTo = onSnapshot(
    query(
      collection(db, COLLECTION),
      where("toUserId", "==", userId),
      ...base
    ),
    (snap) => {
      toRows = mapDocs(snap);
      emit();
    },
    () => {
      toRows = [];
      emit();
    }
  );

  return () => {
    unsubFrom();
    unsubTo();
  };
}
